import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useInView } from "react-intersection-observer";

import { getDispensaryDeals } from "api/requests/getDispensaryDeals";
import { Deal } from "custom-types/Deals";
import formatDeals from "utils/formatDeals";

const useDispensaryDeals = () => {
  const {
    query: { slug },
  } = useRouter();

  /**
   * This ref tracks whether we should avoid making this API call until some
   * element on the page is visible.
   */
  const loadOnScroll = useRef(false);

  const [ref, inView] = useInView({
    triggerOnce: true,
  });

  /**
   * While this isn't an "event", using the `useCallback` hook here prevents
   * the identity of this wrapper ref from changing, like a normal ref would.
   */
  const inViewRef: typeof ref = useCallback((...args) => {
    /**
     * If the ref is used, then we can assume that we shouldn't make the API
     * request until the attached element is in view and so mark this as true.
     */
    loadOnScroll.current = true;
    ref(...args);
  }, []);

  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (slug && (inView || !loadOnScroll.current)) {
      setIsLoading(true);

      getDispensaryDeals(String(slug)).then((response) => {
        setIsLoading(false);
        setDeals(response.data);
      });
    }
  }, [slug, inView]);

  const formattedDeals = formatDeals(deals);

  return {
    hasDeals: deals.length > 0,
    inViewRef,
    isLoading,
    ...formattedDeals,
  };
};

export default useDispensaryDeals;
